// Frigate NVR → SKADE: maskinsyn-deteksjoner fra faste kameraer inn i
// situasjonsbildet.
//
// ── HVORFOR DENNE FILA FINNES ───────────────────────────────────────────────
// Eierkrav 2026-08-17: «det kommer nå en hel haug med kameraer inn fra Frigate
// som skal vises i kartet og man skal kunne klikke på. Når det kommer viktig info
// så skal de sensorene som detekterer noe av interesse blinke opp i noen sekunder
// og med en advarsel som lar operatører velge å rette oppmerksomheten mot denne.»
//
// Frigate kjører objektgjenkjenning LOKALT (Coral TPU / Hailo), så videoen forlater
// aldri kommandoplassen. Det er en OPSEC-egenskap, ikke bare en ytelsesegenskap
// (§4), og den er grunnen til at Frigate er riktig valg framfor en sky-NVR.
//
// ── DEN IKKE-ÅPENBARE DETALJEN SOM AVGJØR DESIGNET ──────────────────────────
// Frigate publiserer på TO tema, og de utfyller hverandre — det er IKKE «nytt vs
// gammelt», som jeg selv skrev i første utkast av mulighetsstudien:
//
//   • `frigate/reviews`  — GRUPPERTE hendelser («en bil parkerte, så gikk en person
//     ut»), med `severity: alert|detection`. Dette er hva man skal VARSLE på: det
//     er den meningsfulle enheten, og det er den som holder varselkøen ren.
//     ⚠ Men den har INGEN bounding-boks. Altså ingen peiling.
//
//   • `frigate/events`   — ENKELTOBJEKTER, med `box` i piksler. Dette er det
//     eneste som kan bli en RETNING i kartet (via kamerakalibrering).
//
// Vi tar derfor imot begge: reviews bestemmer at noe er verdt oppmerksomhet,
// events gir geometrien til å tegne den. Å velge bare det ene gir enten en
// varselkø som drukner i enkeltdeteksjoner, eller markører uten retning.
import { z } from 'zod';
import { KAMERA_REKKEVIDDE_DEFAULT_M, kameraSektorHalvbreddeDeg, peilingTilBoks, } from './kamerakalibrering.js';
import { BILDE_EXTRA_NOKKEL } from './gna-bilde.js';
/** Kildemerke for alt som kom fra en Frigate-instans. */
export const FRIGATE_SOURCE = 'frigate';
/** Hendelsens fase. Samme tre verdier på begge Frigate-temaene. */
export const FrigateFaseSchema = z.enum(['new', 'update', 'end']);
/**
 * ⚠ SKJEMAENE ER BEVISST TOLERANTE (`.passthrough()`, alt utenom kjernen valgfritt).
 *
 * Frigate legger til felt mellom minor-versjoner, og en streng `.strict()` ville
 * gjort at en oppgradering av NVR-en fikk HELE deteksjonsstrømmen til å falle
 * stille bort — verst tenkelige feilmodus for et overvåkingssystem, fordi
 * ingenting synes: kartet er bare tomt, og et tomt kart ser ut som «ingen
 * aktivitet». Vi validerer det vi FAKTISK bruker og lar resten passere.
 */
const FrigateEventEtterSchema = z
    .object({
    id: z.string().min(1).max(200),
    camera: z.string().min(1).max(120),
    label: z.string().min(1).max(80),
    sub_label: z.string().max(80).nullish(),
    /** UNIX-sekunder (flyttall) — Frigate bruker sekunder, vi regner i ms. */
    start_time: z.number(),
    end_time: z.number().nullish(),
    frame_time: z.number().nullish(),
    score: z.number().nullish(),
    top_score: z.number().nullish(),
    /**
     * [x1, y1, x2, y2] i piksler. Dette er det eneste som gir en RETNING.
     *
     * ⚠ IKKE `.length(4)` HER, OG DET ER MED VILJE. En boks med feil form skal
     * koste PEILINGEN, ikke hele deteksjonen: at kamera NØ ser en person er
     * verdt å varsle om selv om geometrien er ubrukelig. Med en streng lengde
     * ville hele meldinga blitt avvist, og operatøren fått ingenting i stedet for
     * noe. Formen valideres i `normaliserFrigate`, som dropper boksen alene.
     * `.max(16)` er kun en vakt mot en absurd stor nyttelast.
     */
    box: z.array(z.number()).max(16).nullish(),
    current_zones: z.array(z.string()).nullish(),
    entered_zones: z.array(z.string()).nullish(),
    has_snapshot: z.boolean().nullish(),
    has_clip: z.boolean().nullish(),
    stationary: z.boolean().nullish(),
    false_positive: z.boolean().nullish(),
})
    .passthrough();
export const FrigateEventSchema = z
    .object({
    type: FrigateFaseSchema,
    before: z.unknown().nullish(),
    after: FrigateEventEtterSchema,
})
    .passthrough();
const FrigateReviewEtterSchema = z
    .object({
    id: z.string().min(1).max(200),
    camera: z.string().min(1).max(120),
    start_time: z.number(),
    end_time: z.number().nullish(),
    /** Frigates egen prioritering: `alert` er verdt oppmerksomhet, `detection` er logg. */
    severity: z.enum(['alert', 'detection']).nullish(),
    thumb_path: z.string().max(500).nullish(),
    data: z
        .object({
        detections: z.array(z.string()).nullish(),
        objects: z.array(z.string()).nullish(),
        sub_labels: z.array(z.string()).nullish(),
        zones: z.array(z.string()).nullish(),
        audio: z.array(z.string()).nullish(),
    })
        .passthrough()
        .nullish(),
})
    .passthrough();
export const FrigateReviewSchema = z
    .object({
    type: FrigateFaseSchema,
    before: z.unknown().nullish(),
    after: FrigateReviewEtterSchema,
})
    .passthrough();
/** Konvolutt for HTTP-ingest: hvilket tema meldinga kom fra. */
export const FrigateInnSchema = z.discriminatedUnion('tema', [
    z.object({ tema: z.literal('reviews'), melding: FrigateReviewSchema }),
    z.object({ tema: z.literal('events'), melding: FrigateEventSchema }),
]);
/** UNIX-sekunder (flyttall) → ms. Frigate bruker sekunder overalt. */
function sekTilMs(s) {
    return Math.round(s * 1000);
}
/**
 * Normaliser en innkommende Frigate-melding.
 *
 * Returnerer `null` når meldinga er ubrukelig — for eksempel et event Frigate
 * selv har flagget som `false_positive`. Å slippe gjennom en kjent falsk positiv
 * ville plassert en markør systemet SELV har sagt er feil.
 */
export function normaliserFrigate(inn) {
    if (inn.tema === 'events') {
        const a = inn.melding.after;
        if (a.false_positive === true)
            return null;
        const boks = a.box && a.box.length === 4 && a.box.every((n) => Number.isFinite(n))
            ? [a.box[0], a.box[1], a.box[2], a.box[3]]
            : undefined;
        // `score` er konfidensen i DENNE rammen, `top_score` er beste gjennom hele
        // hendelsen. Vi tar den høyeste av dem: operatøren skal vurderes mot det
        // beste systemet noen gang så, ikke mot en tilfeldig dårlig ramme.
        const scores = [a.score, a.top_score].filter((n) => typeof n === 'number');
        const objekt = a.sub_label?.trim() ? `${a.label} (${a.sub_label.trim()})` : a.label;
        return {
            id: a.id,
            kameraId: a.camera,
            fase: inn.melding.type,
            objekter: [objekt],
            konfidens: scores.length ? Math.max(...scores) : undefined,
            tsMs: sekTilMs(a.frame_time ?? a.start_time),
            sluttTsMs: typeof a.end_time === 'number' ? sekTilMs(a.end_time) : undefined,
            soner: [...new Set([...(a.current_zones ?? []), ...(a.entered_zones ?? [])])],
            boks,
            erReview: false,
            harSnapshot: a.has_snapshot === true,
        };
    }
    const a = inn.melding.after;
    const objekter = [...(a.data?.objects ?? [])];
    const sub = (a.data?.sub_labels ?? []).filter((s) => s.trim());
    return {
        id: a.id,
        kameraId: a.camera,
        fase: inn.melding.type,
        objekter: objekter.length ? [...objekter, ...sub] : sub,
        tsMs: sekTilMs(a.start_time),
        sluttTsMs: typeof a.end_time === 'number' ? sekTilMs(a.end_time) : undefined,
        soner: [...(a.data?.zones ?? [])],
        // ⚠ reviews har ingen boks — derfor ingen peiling fra dette temaet alene.
        boks: undefined,
        erReview: true,
        frigateAlvor: a.severity ?? undefined,
        harSnapshot: !!a.thumb_path,
    };
}
/**
 * APP-6-symbol for et detektert objekt.
 *
 * ⚠ TILHØRIGHET ER ALLTID UKJENT. Et kamera vet at det står en bil der; det vet
 * ikke hvem sin bil det er. Å utlede `hostile` av at noe dukker opp i en sone
 * ville satt fiendtlige symboler på postbudet — og et situasjonsbilde som roper
 * «fiende» om sivil trafikk blir slått av innen en time.
 */
export function frigateSidc(objekter) {
    const o = objekter.map((s) => s.toLowerCase()).join(' ');
    if (/\bboat|ship|vessel|fartøy|båt\b/.test(o))
        return 'SUSP-----------'; // ukjent overflate (sjø)
    if (/\bperson|people|menneske\b/.test(o))
        return 'SUGPU----------'; // ukjent bakke, enhet
    if (/\bcar|truck|bus|motorcycle|bicycle|kjøretøy\b/.test(o))
        return 'SUGPEV---------'; // ukjent kjøretøy
    if (/\bairplane|aircraft|drone|helicopter\b/.test(o))
        return 'SUAP-----------'; // ukjent luft
    return 'SUGP-------'; // ukjent bakkeobjekt
}
/** Menneskelesbar objektliste til etikett. */
function objektTekst(h) {
    return h.objekter.length ? h.objekter.join(', ') : 'ukjent objekt';
}
/**
 * Frigate-hendelse → spor i situasjonsbildet.
 *
 * ── ÉN MARKØR PER KAMERA, IKKE PER DETEKSJON ────────────────────────────────
 * Sporet er KAMERAET, og deteksjonen er kameraets nåværende tilstand. Det følger
 * eierkravet ordrett: *«de SENSORENE som detekterer noe av interesse skal blinke
 * opp»* — og *«kameraene skal vises i kartet og man skal kunne klikke på»*. Et
 * kamera er et objekt som står der; en person som går forbi er noe det ser.
 *
 * ⚠ FØRSTE UTGAVE HADDE ETT SPOR PER DETEKSJON, OG DET VAR EN EKTE FEIL.
 * Fant den ved at `fase: 'end'`-testen ikke kunne finne sporet sitt:
 * `Hub.snapshot()` kjører `fuseTracks()`, som KLYNGER spor med `kind:
 * 'detection'` på nærhet + tid + type. Alle deteksjoner fra samme kamera ligger
 * på nøyaktig samme koordinat (kameraets), så de ble slått sammen til ETT spor
 * med en syntetisk id. Operatøren ville sett én markør der det var tre
 * hendelser, og `dropp()` på id-en `/ingest/frigate` returnerte ville ikke
 * truffet noe. `kind: 'sensor'` går derimot gjennom fusjonen urørt — som er
 * riktig, for et fast kamera skal aldri fusjoneres med noe.
 *
 * ⚠ SPORET LIGGER PÅ KAMERAET, IKKE PÅ OBJEKTET. Selv med kalibrering vet vi
 * bare RETNINGEN til objektet uten en høydemodell eller vertikal-kalibrering.
 * Siktsektoren peker mot det som ble sett. Vil man ha objektet PLASSERT, må
 * `flatbakkePunkt` (flat flate) eller `estimerMaal` (DEM) kalles av den som HAR
 * de dataene — det er en annen påstand og skal være et annet spor.
 *
 * `fase: 'end'` gir fortsatt et spor: kameraet forsvinner ikke fordi en person
 * gikk ut av bildet. Det går tilbake til hviletilstand, uten aktiv deteksjon.
 */
export function frigateTilSpor(h, opts = {}) {
    const k = opts.kalibrering;
    const kameranavn = k?.navn?.trim() || h.kameraId;
    const aktiv = h.fase !== 'end';
    const spor = {
        // ÉN id per kamera. Stabil gjennom alle hendelser, så markøren oppdateres i
        // stedet for å stables — og så `dropp()` treffer det kalleren tror.
        id: `frigate:${h.kameraId}`,
        // `sensor`, ikke `detection`: se fusjons-merknaden over.
        kind: 'sensor',
        // Se `frigateSidc`: et kamera kjenner ikke tilhørighet.
        affiliation: 'unknown',
        // Uten kalibrering har vi ingen posisjon i det hele tatt. Da er 0,0 feil og
        // en utelatelse umulig (Track krever lat/lon), så kalleren MÅ ha kalibrering
        // — se `frigateHarPosisjon`.
        lat: k?.lat ?? 0,
        lon: k?.lon ?? 0,
        // Symbolet følger det kameraet SER nå; i hvile er det bare et kamera.
        sidc: aktiv ? frigateSidc(h.objekter) : 'SUGP-------',
        label: aktiv ? `${kameranavn}: ${objektTekst(h)}` : kameranavn,
        tsMs: h.tsMs,
        source: FRIGATE_SOURCE,
        confidence: aktiv ? h.konfidens : undefined,
        // ⚠ BEVARES: et innmålt kamera er INFRASTRUKTUR, ikke et øyeblikk. Det skal
        // ligge i kartet også når det ikke ser noe — ellers forsvinner halve
        // sensordekningen fra situasjonsbildet hver gang det er stille, og operatøren
        // kan ikke se forskjell på «kameraet ser ingenting» og «kameraet er nede».
        bevares: true,
        // Frigates EGEN vurdering følger med sporet, så varselkøen i COP-en bruker
        // den i stedet for en sentral default. `alert` = verdt oppmerksomhet,
        // `detection` = logg. Ingenting fra et kamera blir `kritisk`: et maskinsyn som
        // roper KRITISK om en bil på en parkeringsplass ødelegger køen for de
        // varslene som faktisk er kritiske.
        ...(aktiv
            ? { varselAlvor: h.frigateAlvor === 'alert' ? 'advarsel' : 'info' }
            : {}),
    };
    if (opts.org)
        spor.org = opts.org;
    const extra = {
        Kilde: `Frigate · ${kameranavn}`,
        // Hviletilstand SIES. Et tomt felt ville lest som «vi vet ikke», og
        // operatøren skal kunne skille «kameraet ser ingenting» fra «kameraet er nede».
        Objekt: aktiv ? objektTekst(h) : 'ingen aktiv deteksjon',
    };
    if (aktiv && h.konfidens !== undefined) {
        extra.Konfidens = `${Math.round(h.konfidens * 100)} %`;
    }
    if (aktiv) {
        // Frigates egen hendelses-id, så en markør kan spores tilbake til klippet i
        // NVR-en. Sporet har KAMERAETS id, så uten dette feltet var koblingen borte.
        extra['Frigate-id'] = h.id;
    }
    if (aktiv && h.soner.length)
        extra.Soner = h.soner.join(', ');
    if (aktiv && h.frigateAlvor) {
        extra['Frigate-alvor'] = h.frigateAlvor === 'alert' ? 'alert (verdt oppmerksomhet)' : 'detection (logg)';
    }
    // Samme nøkkel som feltbilder, så COP rendrer Frigate-snapshot som bilde òg.
    if (opts.snapshotUrl)
        extra[BILDE_EXTRA_NOKKEL] = opts.snapshotUrl;
    // Siktsektor: bare når kameraet er innmålt. Peilingen krever i tillegg en boks,
    // som bare `events` har — uten den peker sektoren midt i bildet, som er det
    // ærlige «et sted i dette kameraets synsfelt».
    if (k) {
        // I hvile viser sektoren kameraets DEKNING (midtlinje + helt synsfelt); under
        // en aktiv deteksjon smalnes den inn mot der objektet faktisk ble sett.
        const harRetning = aktiv && !!h.boks;
        const bearingDeg = harRetning ? peilingTilBoks(k, h.boks) : k.asimutDeg;
        const sektor = {
            kind: 'sektor',
            origin: { lat: k.lat, lon: k.lon },
            bearingDeg,
            // Med boks: monterings-usikkerheten. Uten boks: hele synsfeltet, fordi
            // objektet kan være hvor som helst i bildet.
            bearingSigmaDeg: harRetning ? kameraSektorHalvbreddeDeg(k) : k.hfovDeg / 2,
            rMinM: 0,
            rMaxM: k.rekkeviddeM ?? KAMERA_REKKEVIDDE_DEFAULT_M,
        };
        spor.siktsektor = sektor;
        extra.Retning = harRetning
            ? `${Math.round(bearingDeg)}° sann (fra bildeposisjon)`
            : `${Math.round(bearingDeg)}° sann (kameraets midtlinje — ${aktiv ? 'ingen bildeposisjon' : 'dekningsretning'})`;
        extra.Rekkevidde = k.rekkeviddeM !== undefined
            ? `${Math.round(k.rekkeviddeM)} m (kameraets oppgitte rekkevidde)`
            : 'ikke målt — sektoren er tegnet med fast lengde';
    }
    else {
        // ⚠ SIES EKSPLISITT. Et ukalibrert kamera er ikke et defekt kamera; det er et
        // kamera ingen har målt inn ennå, og deteksjonen er fortsatt verdt å varsle om.
        extra['⚠ Kalibrering'] = 'Kameraet er ikke innmålt — ingen retning og ingen posisjon';
    }
    spor.extra = extra;
    return spor;
}
/**
 * Kan denne hendelsen i det hele tatt plasseres i kartet?
 *
 * Kalleren MÅ sjekke denne før `frigateTilSpor` brukes til å absorbere et spor:
 * uten kalibrering finnes ingen posisjon, og `Track` har ingen måte å uttrykke
 * «ukjent sted» (det er hva `SporUtenPosisjon` er til, og det laget er ikke wiret
 * ennå — se CMO-02). Varselet skal likevel sendes: at et ukalibrert kamera ser
 * noe er informasjon, selv uten kartmarkør.
 */
export function frigateHarPosisjon(opts) {
    return !!opts.kalibrering;
}
// ⚠ HER LÅ EN `frigateTilAlert()`. Den er FJERNET, med vilje.
//
// Hubben har ingen varselkanal for Track: varselkøen bygges KLIENTSIDIG i
// COP-en (`buildAlertFeed` over sporene den alt har). En egen alert-bygger her
// ville derfor blitt en eksportert funksjon uten kallsted — presis den slags
// «ferdig motor, ingen ledning» som revisjonen 2026-08-17 fant 64 av.
//
// I stedet bærer sporet `varselAlvor`, og den generiske regelen
// `sensorObservasjonAlerts` leser den. Da gjelder Frigates lokale vurdering
// automatisk, og hver ny sensortype med en sektor får varsler uten ny kode.
// (VALE SENS-21: `frigateTilSporUtenPosisjon` under er den TILSVARENDE
// ledningen for det ukalibrerte tilfellet — via `uposisjonertKontaktAlerts`
// i `alerts.ts`, ikke en egen bygger her heller.)
/**
 * Frigate-hendelse UTEN kalibrering → `SporUtenPosisjon` (VALE SENS-21).
 *
 * ⚠ HVORFOR IKKE fusjonslagets `kontaktTilSporUtenPosisjon()`/`usikkerhetsformer()`
 * (fusion/multisensor.ts §8, se `vale/CMO_VURDERING.md`)? Den maskineriet løser
 * et ANNET problem: en `Kontakt` bygd av `Observasjon[]` der `sensorLat`/
 * `sensorLon` er OBLIGATORISK (`ObservasjonPeiling`/`ObservasjonVisuell`) — altså
 * at vi VET hvor SENSOREN står, men ikke hvor MÅLET er (bearing-only). Et
 * kamera Frigate selv aldri har fått en `Kamerakalibrering` for oppfyller ikke
 * det: vi har IKKE ETT kjent punkt å feste `Usikkerhetsform.origin` til, så
 * `usikkerhetsformer()` ville ikke hatt noe å regne fra — den fungerer allerede
 * for det bearing-only-tilfellet ved siden av dette (kalibrert kamera, ingen
 * DEM), som `frigateTilSpor` + `Track.siktsektor` + `sensorObservasjonAlerts`
 * dekker i dag. Å dikte en origin her for å «passe inn» i fusjonens type ville
 * vært akkurat den oppdiktede presisjonen §1 forbyr. `usikkerhet` er derfor
 * BEVISST en tom liste — ingen geometri finnes, og ingen skal late som den gjør.
 * Et fremtidig kartlag (CMO-02) kan trygt tegne null flater for denne.
 *
 * Returnerer `null` når kalibrering FINNES (bruk `frigateTilSpor` i stedet — de
 * to er gjensidig utelukkende, som `Track`/`SporUtenPosisjon` ellers) eller for
 * `fase: 'end'` (hviletilstand for et kamera vi ikke har noen markør for i
 * utgangspunktet — ingenting NYTT å varsle om).
 */
export function frigateTilSporUtenPosisjon(h, opts = {}) {
    if (opts.kalibrering)
        return null;
    if (h.fase === 'end')
        return null;
    const extra = { Objekt: objektTekst(h) };
    if (h.konfidens !== undefined)
        extra.Konfidens = `${Math.round(h.konfidens * 100)} %`;
    extra['Frigate-id'] = h.id;
    if (h.soner.length)
        extra.Soner = h.soner.join(', ');
    if (h.frigateAlvor) {
        extra['Frigate-alvor'] = h.frigateAlvor === 'alert' ? 'alert (verdt oppmerksomhet)' : 'detection (logg)';
    }
    const spor = {
        // Samme idiom som `frigateTilSpor`: ÉTT spor per KAMERA, ikke per deteksjon,
        // så en operatør ser kameraets NÅVÆRENDE tilstand oppdateres, ikke stables.
        id: `frigate:${h.kameraId}`,
        kind: 'detection',
        tsMs: h.tsMs,
        source: FRIGATE_SOURCE,
        usikkerhet: [],
        label: `⚠ UKALIBRERT KAMERA «${h.kameraId}»: ${objektTekst(h)}`,
        begrunnelse: `Kamera «${h.kameraId}» er ikke innmålt (mangler kalibrering) — ingen posisjon og ` +
            'ingen retning kan oppgis. Deteksjonen varsles likevel, så den ikke blir usynlig for operatøren.',
        varselAlvor: h.frigateAlvor === 'alert' ? 'advarsel' : 'info',
        extra,
    };
    if (opts.org)
        spor.org = opts.org;
    return spor;
}
/**
 * Sti til snapshot-bildet i Frigates eget REST-API.
 *
 * Hubben henter dette og lagrer det i SITT medialager, framfor å la COP-en peke
 * rett på NVR-en: Frigate står typisk på et lukket nett operatørens nettleser
 * ikke ser, og en direkte lenke ville gitt et ødelagt bilde uten forklaring.
 */
export function frigateSnapshotSti(h) {
    // Bare events har et snapshot av ETT objekt; reviews har en thumbnail av
    // hendelsen. Frigates API-sti er den samme for begge id-typene.
    if (!h.harSnapshot)
        return null;
    return `/api/events/${encodeURIComponent(h.id)}/snapshot.jpg`;
}
//# sourceMappingURL=frigate.js.map